import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ENV_CONFIG } from '@ps/core-config';
import type { SimilarSurvey } from '@ps/survey-utils';

import type {
  SimilarSurveyDto,
  SimilarSurveysRequestDto,
} from './dtos/similar-surveys.dto';
import { SimilarSurveyMapper } from './mappers/similar-survey.mapper';

@Injectable({ providedIn: 'root' })
export class SimilarSurveysApi {
  private readonly _env = inject(ENV_CONFIG);
  private readonly _http = inject(HttpClient);

  private readonly _url = `${this._env.pureSpectrum.url}/buyers/v2/similar-projects`;
  private readonly _mapper = new SimilarSurveyMapper();

  /**
   * No loading context on purpose: the Similar Survey panel renders its own
   * skeleton, and this runs unprompted whenever a survey is opened — a global
   * overlay would block the drawer for a search the buyer did not ask for.
   */
  public search(
    payload: SimilarSurveysRequestDto,
    { searchGlobally }: { searchGlobally: boolean },
  ): Observable<SimilarSurvey[]> {
    return this._http
      .post<SimilarSurveyDto[]>(this._url, payload, {
        params: this._params(searchGlobally),
      })
      .pipe(map((dtos) => this._mapper.fromDtoList(dtos)));
  }

  /**
   * The endpoint keys off the parameter being present, so an account-scoped
   * search omits it rather than sending `global=0` — `HttpParams` would
   * stringify `false` to the literal `"false"`, which reads as truthy.
   */
  private _params(searchGlobally: boolean): HttpParams {
    const params = new HttpParams();
    return searchGlobally ? params.set('global', 1) : params;
  }
}
